import React, { useCallback, useMemo, useState } from 'react';
import { IItemForGetShortInfo } from 'types';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import SearchField from 'components/SearchField/SearchField';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import style from './MultipleSelect.module.scss';

interface IProps {
	data: IItemForGetShortInfo[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setModifiers: (value: string[]) => void;
	modifiers: string[];
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({ data, isLoading, refetch, responseError, setModifiers, modifiers }) => {
		const [searchString, setSearchString] = useState<string>('');

		const filteredModifiers = useMemo(() => {
			return searchString
				? data.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: data;
		}, [searchString, data]);

		const handleSelectAllModifiers = useCallback(() => {
			if (data.length === modifiers.length) {
				setModifiers([]);
			} else {
				const IDsOfAllModifiers = data.map((el) => el.id);
				setModifiers(IDsOfAllModifiers);
			}
		}, [setModifiers, data, modifiers]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				const getIsCheckedByHasAccess = () => {
					return !!(data.length && data.length === modifiers.length);
				};

				return (
					<>
						<div className={style.selectMenuHeader}>
							<Checkbox
								className={style.checkBox}
								checked={getIsCheckedByHasAccess()}
								disabled={!data.length}
								onChange={handleSelectAllModifiers}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllModifiersLabel"
									asFragment={true}
								/>
							</Checkbox>
							<SearchField
								onChangeSelectSearchString={setSearchString}
								searchString={searchString}
								className={style.searchField}
							/>
						</div>
						{menu}
					</>
				);
			},
			[data, modifiers, handleSelectAllModifiers, setSearchString, searchString],
		);

		return (
			<OutsideFormSelect
				testId="MultipleSelect"
				name="modifiers"
				label="modifiersLabel"
				value={modifiers}
				options={filteredModifiers}
				fetchError={responseError}
				loading={isLoading}
				onRefetch={refetch}
				disabled={isLoading}
				onChange={setModifiers}
				counterValue={modifiers.length}
				withValidation={false}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
